import {
  BoundMultiSelectField,
  Button,
  cardStyle,
  CollapseToggle,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  multiFilter,
  NumberField,
  SelectField,
  SelectToggle,
  singleFilter,
  Switch,
  Tag,
  Tooltip,
  useComputed,
  useFilter,
  useGridTableApi,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useEffect, useMemo, useState } from "react";
import { Icon, SearchBox } from "src/components";
import { useLocationsQuery } from "src/components/autoPopulateSelects/LocationsSelectField.generated";
import { useStepperContext } from "src/components/stepper";
import {
  type CheckoutConfigOptionGroupStepDetail,
  Maybe,
  SaveProductOfferingInput,
  type SaveProductOfferingOptionGroupInput,
  SaveProductOfferingOptionInput,
} from "src/generated/graphqlTypes";
import { useReaction } from "src/hooks";
import { useMatchAny } from "src/hooks/useMatchAny";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BidOutCell } from "src/routes/developments/product-offerings/components/BidOutCell";
import { PrerequisitesTag } from "src/routes/developments/product-offerings/components/PrerequisitesTag";
import { ConflictTag } from "src/routes/developments/product-offerings/components/ReadOnlyOptionsTable";
import { RequiredOptionsTag } from "src/routes/developments/product-offerings/components/RequiredOptionsTag";
import { TableActions } from "src/routes/layout/TableActions";
import {
  CreateOfferingReadyPlanOptionFragment,
  useOptionsStepQuery,
} from "src/routes/libraries/product-offerings/add-product-offering/components/4.OptionsStep.generated";
import { EditProductOfferingPage_BaseConfigFragment } from "src/routes/libraries/product-offerings/edit-product-offering/EditProductOfferingPage.generated";
import { ProductOfferingParams } from "src/routes/routesDef";
import { productOfferingEditRoute } from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { StringParam, useQueryParams } from "use-query-params";
import { EditProductOfferingStep } from "../utils";
import { ProductOfferingStepActions } from "./ProductOfferingStepActions";

type OptionsStepProps = { formState: ObjectState<SaveProductOfferingInput> };

export function OptionsStep({ formState }: OptionsStepProps) {
  const result = useOptionsStepQuery({
    variables: {
      input: {
        planPackageId: formState.planPackageId.value!,
        designPackageIds: [formState.exteriorDesignPackageId.value!, formState.interiorDesignPackageId.value!],
      },
    },
  });
  return queryResult(result, ({ potentialOptions }) => (
    <OptionsView readyPlanOptions={potentialOptions} formState={formState} baseConfig={undefined} readOnly={false} />
  ));
}

type OptionsViewProps = {
  /**
   * Create view:
   * - All options available from stepper selected plan package and design package(s) (POF.potentialOptions)
   *
   * Edit view:
   * - All options that are available to be added to the pof (POF.options)
   *  **/
  readyPlanOptions: CreateOfferingReadyPlanOptionFragment[];
  formState: ObjectState<SaveProductOfferingInput>;
  /**
   * Only used in edit view to determine which options can be removed from POF or toggled in checkout
   */
  baseConfig: Maybe<EditProductOfferingPage_BaseConfigFragment>;
  /** When true, renders the view in read-only mode */
  readOnly: boolean;
  /** Reasons why the view is read-only, displayed in a tooltip */
  disabledReasons?: string[];
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[];
};

export function OptionsView({
  readyPlanOptions,
  formState,
  baseConfig,
  readOnly,
  disabledReasons = [],
  checkoutConfigOptionGroupSteps,
}: OptionsViewProps) {
  const tableApi = useGridTableApi<Row>();
  const { productOfferingId } = useRouteParams<ProductOfferingParams>();
  const optionsTypes = useMemo(() => readyPlanOptions.map((rpo) => rpo.type).unique() ?? [], [readyPlanOptions]);

  const locationsQuery = useLocationsQuery();

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const filterDefs: FilterDefs<OptionTypeFilter> = useMemo(() => {
    const option = multiFilter({
      label: "Type",
      options: optionsTypes,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const location = multiFilter({
      label: "Location",
      options: locationsQuery?.data?.locations ?? [],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const showOptions = singleFilter({
      label: "Show Options",
      options: [
        { id: "disabled", name: "Disabled" },
        { id: "archived", name: "Archived" },
        { id: "not-in-checkout", name: "Not in Checkout" },
        { id: "not-in-underwriting", name: "Not in Underwriting" },
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id as ShowOptionsFilterValue,
    });
    return { option, location, showOptions };
  }, [locationsQuery?.data?.locations, optionsTypes]);

  const { filter, setFilter } = useFilter({ filterDefs });
  const [collapsedGroups, setCollapsedGroups] = useState(false);

  const tableFormState = useFormState({
    config: tableFormConfig,
    init: {
      onlyOnce: true,
      input: {
        readyPlanOptions: readyPlanOptions.map((o) => ({
          id: o.id,
          optionGroup: { ...o.optionGroup, overrideStep: o.optionGroup.overrideStep?.code },
          optionDefaultsIfIds: o.optionDefaultsIf.map(({ id }) => id),
          order: o.order,
          publishToUnderwriting: o.publishToUnderwriting,
          showInCheckout: o.showInCheckout,
          checkoutOrder: o.checkoutOrder,
        })),
      },
    },
  });

  // On initial load either select all options if none have been selected or select the ones that have been selected
  useEffect(() => {
    let activeReadyPlanOptions = readyPlanOptions.filter((rpo) => rpo.active);
    if (formState.options.value.length > 0) {
      const selectedReadyPlanOptions = formState.options.value.map((e) => e.id);
      activeReadyPlanOptions = activeReadyPlanOptions.filter((rpo) => selectedReadyPlanOptions.includes(rpo.id));
    }
    activeReadyPlanOptions
      .filter((rpo) => rpo.optionGroup.isMultiOptionGroup)
      .forEach((rpo) => tableApi.selectRow(rpo.id, true));
    activeReadyPlanOptions
      .filter((rpo) => rpo.optionGroup.isSingleOptionGroup)
      .forEach((rpo) => tableApi.selectRow(rpo.optionGroup.id, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // It would be nice to use a single form state but because we want to persist the defaults of unselected options we need to use a separate form state
  useReaction(
    () => {
      const selectedChildRpos = tableApi.getSelectedRows("child");
      const selectedParentRpogs = tableApi.getSelectedRows("parent");
      const selectedRpoIds = selectedChildRpos
        .map((row) => row.id)
        .concat(selectedParentRpogs.map((row) => row.data.rpo.id));
      return tableFormState.readyPlanOptions.value.filter((rpo) => selectedRpoIds.includes(rpo.id));
    },
    (options) => {
      // Sanity check we don't update the formState when in read-only mode
      if (readOnly) return;
      formState.options.set(
        options.map(({ id, optionDefaultsIfIds, publishToUnderwriting, showInCheckout }) => ({
          id,
          optionDefaultsIfIds,
          publishToUnderwriting,
          showInCheckout,
        })),
      );
    },
    [tableApi, tableFormState, readOnly],
    { fireImmediately: true },
  );

  return (
    <>
      <div css={Css.df.fdc.aic.$}>
        <div css={Css.xl2.$}>{readOnly ? "View your options" : "Select your options"}</div>
        <div css={Css.md.pt2.pbPx(60).df.aic.gap1.$}>
          {readOnly ? (
            <>
              Viewing the option configuration for this offering.
              {disabledReasons.nonEmpty && (
                <Tooltip
                  title={
                    <div>
                      {disabledReasons.map((reason, i) => (
                        <div key={i}>{reason}</div>
                      ))}
                    </div>
                  }
                >
                  <Icon icon="infoCircle" />
                </Tooltip>
              )}
            </>
          ) : (
            "Search and select the various option types to add to the plan. All conflicts and dependencies are already attached."
          )}
        </div>
      </div>
      <TableActions>
        <div css={Css.df.gap1.jcfe.$}>
          <SearchBox onSearch={setSearchFilter} clearable />
          <Filters<OptionTypeFilter> filter={filter} filterDefs={filterDefs} onChange={setFilter} />
        </div>
        <div css={Css.df.aic.gap2.$}>
          <Button
            onClick={() => setCollapsedGroups(!collapsedGroups)}
            icon={collapsedGroups ? "expand" : "collapse"}
            label={`${collapsedGroups ? "Expand" : "Collapse"} Groups`}
            variant="secondary"
          />
        </div>
      </TableActions>
      <div css={Css.w100.h100.bgWhite.p4.$}>
        <OptionsTable
          collapsedGroups={collapsedGroups}
          tableFormState={tableFormState}
          formState={formState}
          readyPlanOptions={readyPlanOptions}
          filter={filter}
          searchFilter={searchFilter}
          tableApi={tableApi}
          baseConfig={baseConfig}
          readOnly={readOnly}
          checkoutConfigOptionGroupSteps={checkoutConfigOptionGroupSteps}
        />
      </div>
      {!productOfferingId && <ProductOfferingStepActions />}
    </>
  );
}

type OptionsTableProps = {
  tableFormState: ObjectState<FormValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readyPlanOptions: CreateOfferingReadyPlanOptionFragment[];
  searchFilter?: string;
  filter: OptionTypeFilter;
  collapsedGroups: boolean;
  tableApi: GridTableApi<Row>;
  baseConfig?: EditProductOfferingPage_BaseConfigFragment | null;
  readOnly: boolean;
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[];
};

function OptionsTable(props: OptionsTableProps) {
  const {
    tableFormState,
    formState,
    readyPlanOptions = [],
    filter,
    searchFilter = "",
    collapsedGroups,
    tableApi,
    baseConfig,
    checkoutConfigOptionGroupSteps,
  } = props;
  const isEditPath = !!useMatchAny([productOfferingEditRoute.path]);
  const [{ projectId, devId }] = useQueryParams({ projectId: StringParam, devId: StringParam });
  const rpos = useComputed(() => tableFormState.readyPlanOptions.rows, [tableFormState]);
  const { currentStep } = useStepperContext();
  const readOnly = props.readOnly || currentStep.value === EditProductOfferingStep.REVIEW;
  const rows: GridDataRow<Row>[] = useMemo(
    () => createRows(readyPlanOptions, rpos, filter, baseConfig),
    [rpos, filter, readyPlanOptions, baseConfig],
  );
  const columns = useMemo(() => {
    return createColumns(
      readyPlanOptions,
      formState,
      readOnly,
      isEditPath,
      checkoutConfigOptionGroupSteps,
      devId || undefined,
      projectId || undefined,
    );
  }, [readyPlanOptions, formState, readOnly, isEditPath, checkoutConfigOptionGroupSteps, devId, projectId]);

  useEffect(() => {
    const [collapsed, expanded] = rows.partition((r) => tableApi.isCollapsedRow(r.id));
    const target = collapsedGroups ? expanded : collapsed;
    target.forEach((r) => tableApi.toggleCollapsedRow(r.id));
  }, [tableApi, collapsedGroups, rows]);

  return (
    <GridTable
      api={tableApi}
      rows={rows}
      columns={columns}
      filter={searchFilter}
      style={tableStyles}
      rowStyles={rowStyles}
      as="virtual"
    />
  );
}

enum Columns {
  Select,
  Name,
  Details,
  DefaultOf,
  Publication,
  Checkout,
  CheckoutStepOverride,
  BidPercent,
}

function createColumns(
  rpos: CreateOfferingReadyPlanOptionFragment[],
  formState: ObjectState<SaveProductOfferingInput>,
  readOnly: boolean,
  isEditPath: boolean,
  checkoutConfigOptionGroupSteps?: CheckoutConfigOptionGroupStepDetail[],
  devId?: string,
  projectId?: string,
): GridColumn<Row>[] {
  return [
    ...maybeAddIdColumn(
      column<Row>({ header: "Id", parent: ({ rpo }) => rpo.optionGroup.id, child: ({ rpo }) => rpo.id, w: "92px" }),
    ),
    column<Row>({
      id: `${Columns.Name}`,
      header: "Name",
      parent: ({ rpo, usedInBaseConfig }, row) => ({
        content: () => (
          <div css={Css.df.aic.gap2.$}>
            <CollapseToggle row={row.row} />
            <SelectToggle
              id={row.row.id}
              disabled={readOnly || !!rpo.latestVersion?.isUpstreamInactive || !!usedInBaseConfig}
            />
            {/* Ideally we would have separate Row types for multi-option/single-option groups... */}
            {rpo.optionGroup.isMultiOptionGroup && (
              <div css={Css.df.fdc.gap1.$}>
                <div /* use archive as a 'group' icon */ css={Css.df.aic.$}>
                  <Icon icon="archive" xss={Css.mrPx(5).$} />
                  <span css={Css.lg.$}>{rpo.optionGroup.name}</span>
                </div>
                {rpo.optionGroup.readyPlanLocation && (
                  <div css={Css.df.gap1.aic.$}>
                    <Tag text={rpo.optionGroup.readyPlanLocation.location.displayLocationPath} type="neutral" />
                  </div>
                )}
              </div>
            )}
            {rpo.optionGroup.isSingleOptionGroup && (
              <div css={Css.df.fdc.$}>
                <span css={Css.lg.$}>{rpo.optionGroup.name}</span>
                <div css={Css.df.gap1.aic.$}>
                  <div css={Css.xs.$}>{rpo.globalOption.code}</div>
                  {rpo.latestVersion?.isUpstreamInactive && <Tag text="Archived" type="warning" />}
                  {rpo.optionGroup.readyPlanLocation && (
                    <Tag text={rpo.optionGroup.readyPlanLocation.location.displayLocationPath} type="neutral" />
                  )}
                </div>
              </div>
            )}
          </div>
        ),
        value: `${rpo.optionGroup.name} ${rpo.globalOption.code}`,
        css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
      }),
      child: ({ rpo, usedInBaseConfig }, { row }) => ({
        content: () => (
          <div css={Css.df.aic.gap2.$}>
            <SelectToggle
              id={row.id}
              // Checkout !rpo.active to allow not-yet-inactive rpos to be turned off
              disabled={readOnly || (!!rpo.latestVersion?.isUpstreamInactive && !rpo.active) || !!usedInBaseConfig}
            />
            <div css={Css.df.fdc.$}>
              <span css={Css.mdSb.$}>{rpo.name}</span>
              <div css={Css.df.gap1.aic.$}>
                <span css={Css.xs.$}>{rpo.globalOption.code}</span>
                {rpo.latestVersion?.isUpstreamInactive && <Tag text="Archived" type="warning" />}
              </div>
            </div>
          </div>
        ),
        value: `${rpo.name} ${rpo.globalOption.code}`,
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "300px",
    }),
    column<Row>({
      id: `${Columns.Details}`,
      header: "Details",
      parent: ({ rpo }) => ({
        content: () => <OptionDetailsTags rpo={rpo} isGroupRow />,
        value: optionDetailsTags(rpo, true)
          .map((tag) => tag.textValue)
          .join(" "),
        css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
      }),
      child: ({ rpo }) => ({
        content: () => <OptionDetailsTags rpo={rpo} />,
        value: optionDetailsTags(rpo, false)
          .map((tag) => tag.textValue)
          .join(" "),
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "220px",
      mw: "220px",
    }),
    column<Row>({
      id: `${Columns.BidPercent}`,
      header: isEditPath ? "Percent Bid" : "",
      parent: ({ rpo }) => ({
        content:
          isEditPath && rpo.optionGroup.isSingleOptionGroup && (devId || projectId) ? (
            <BidOutCell bidOutPercentage={rpo.bidOutPercentage} developmentId={devId} projectId={projectId} />
          ) : (
            <></>
          ),
      }),
      child: ({ rpo }) => ({
        content: () =>
          isEditPath && (devId || projectId) ? (
            <BidOutCell bidOutPercentage={rpo.bidOutPercentage} developmentId={devId} projectId={projectId} />
          ) : (
            <></>
          ),
      }),
      w: "200px",
    }),
    column<Row>({
      id: `${Columns.Publication}`,
      header: isEditPath ? "Publish to Underwriting" : "",
      parent: ({ rpo, state }) => ({
        content:
          isEditPath && rpo.optionGroup.isSingleOptionGroup ? (
            <PublishRPOToUWCheckbox rpoState={state} formState={formState} readOnly={readOnly} />
          ) : (
            <></>
          ),
      }),
      child: ({ state }) => ({
        content: () =>
          isEditPath ? <PublishRPOToUWCheckbox rpoState={state} formState={formState} readOnly={readOnly} /> : <></>,
      }),
      w: "200px",
    }),
    column<Row>({
      id: `${Columns.Checkout}`,
      header: "Checkout",
      w: "220px",
      mw: "220px",
      parent: ({ state, usedInBaseConfig, childStates }) => ({
        content: (
          <div css={Css.df.fdc.gap2.$}>
            <ShowRPOGInCheckoutCheckbox
              rpoState={state}
              usedInBaseConfig={usedInBaseConfig}
              formState={formState}
              readOnly={readOnly}
              rpos={rpos}
              childStates={childStates}
            />
            <CheckoutRPOGOrderField rpoState={state} formState={formState} readOnly={readOnly} />
          </div>
        ),
      }),
      child: ({ state, rpo, usedInBaseConfig }) => ({
        content: () => (
          <div css={Css.df.fdc.gap2.w100.$}>
            <ShowRPOInCheckoutCheckbox
              usedInBaseConfig={usedInBaseConfig}
              rpoState={state}
              formState={formState}
              readOnly={readOnly}
            />
            <CheckoutRPOOrderField
              rpoState={state}
              formState={formState}
              readOnly={readOnly}
              isPersonalizationOption={!!rpo.optionGroup.personalizationType}
            />
          </div>
        ),
      }),
    }),
    ...(isEditPath && checkoutConfigOptionGroupSteps
      ? [
          column<Row>({
            id: `${Columns.CheckoutStepOverride}`,
            header: "Checkout Step",
            w: "240px",
            mw: "240px",
            parent: ({ state }) => ({
              content: () => (
                <div css={Css.df.fdc.gap2.w100.$}>
                  <OverrideStepField
                    rpoState={state}
                    formState={formState}
                    readOnly={readOnly}
                    checkoutConfigOptionGroupSteps={checkoutConfigOptionGroupSteps}
                  />
                  <OverrideSubstepNumberField rpoState={state} formState={formState} readOnly={readOnly} />
                </div>
              ),
            }),
            child: () => emptyCell,
          }),
        ]
      : []),
    column<Row>({
      id: `${Columns.DefaultOf}`,
      header: "Default of",
      parent: ({ rpo, state }) =>
        // show same default of select if group only has 1 member
        rpo.optionGroup.isSingleOptionGroup
          ? {
              content: () => <DefaultOfSelectField rpo={state} rpos={rpos} formState={formState} readOnly={readOnly} />,
              value: state.optionDefaultsIfIds.value?.toString(),
              css: Css.sm.if(showHighlights({ rpo, isGroupRow: true, isEditPath })).bgYellow50.$,
            }
          : { content: rpo.optionGroup.required ? "Required" : undefined, alignment: "right" },
      child: ({ state, rpo }) => ({
        content: () => <DefaultOfSelectField rpo={state} rpos={rpos} formState={formState} readOnly={readOnly} />,
        value: state.optionDefaultsIfIds.value?.toString(),
        css: Css.sm.if(showHighlights({ rpo, isEditPath })).bgYellow50.$,
      }),
      w: "200px",
    }),
  ];
}

function createRows(
  rpos: CreateOfferingReadyPlanOptionFragment[],
  rpoObjectStates: readonly ObjectState<FormOptionValue>[],
  filter: OptionTypeFilter,
  baseConfig?: EditProductOfferingPage_BaseConfigFragment | null,
): GridDataRow<Row>[] {
  // Group by option groups
  const optionGroups = rpos
    .filter((rop) => filter.location?.includes(rop.optionGroup.readyPlanLocation?.location.id ?? "") ?? true)
    .filter((rpo) => {
      // When a showOptions filter is selected, strictly show only matching RPOs
      if (filter.showOptions === "archived") return !!rpo.latestVersion?.isUpstreamInactive;
      if (filter.showOptions === "disabled") return !rpo.active;
      if (filter.showOptions === "not-in-checkout") return !rpo.showInCheckout;
      if (filter.showOptions === "not-in-underwriting") return !rpo.publishToUnderwriting;
      // Default: hide archived/disabled, apply type filter
      return (!rpo.latestVersion?.isUpstreamInactive || rpo.active) && (filter.option?.includes(rpo.type.id) ?? true);
    })
    .groupBy((o) => o.optionGroup.id);

  // Create a map of rpo.id => its object state to avoid n^2 lookups
  const rpoStatesById = rpoObjectStates.keyBy((o) => o.id.value);
  // Create a set to avoid n^2 lookups
  const baseConfigRpoIds = new Set(baseConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? []);

  // sort by group order
  return Object.entries(optionGroups)
    .sortBy(([, rpos]) => rpos[0].optionGroup.order)
    .map(([optionGroupId, rpos]) => {
      return {
        kind: "parent" as const,
        data: {
          // Because a parent row can be either a "multi-option group actual parent row" or single-option group fake
          // parent row", we always use the 1st rpo as "the parent".
          rpo: rpos[0],
          state: rpoStatesById[rpos[0].id],
          childStates: rpos.map((rpo) => rpoStatesById[rpo.id]),
          // NOTE: This logic could go away were we to prompt users to update configs that will become invalidated
          // by these updates. Not just `showInCheckout` but also if the option is available on the POF in the first place
          usedInBaseConfig: rpos.some((rpo) => baseConfigRpoIds.has(rpo.id))
            ? `Disabled because starting config "${baseConfig?.name}" is using this option`
            : undefined,
        },
        id: optionGroupId,
        children: rpos[0].optionGroup.isMultiOptionGroup
          ? rpos
              .flatMap((rpo) => ({
                kind: "child" as const,
                id: rpo.id,
                data: {
                  rpo: rpo,
                  state: rpoStatesById[rpo.id],
                  // NOTE: This logic could go away were we to prompt users to update configs that will become invalidated
                  // by these updates. Not just `showInCheckout` but also if the option is available on the POF in the first place
                  usedInBaseConfig: baseConfigRpoIds.has(rpo.id)
                    ? `Disabled because starting config "${baseConfig?.name}" is using this option`
                    : undefined,
                },
              }))
              .sortBy((rpo) => rpo.data.state.order.value ?? Number.POSITIVE_INFINITY)
          : undefined,
      };
    });
}

type RpoSelectFieldProps = {
  rpo: ObjectState<FormOptionValue>;
  rpos: CreateOfferingReadyPlanOptionFragment[];
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function DefaultOfSelectField({ rpo, rpos, formState, readOnly }: RpoSelectFieldProps) {
  // If the formState defaultIds have been set, display those
  const formRpoDefaultIds = formState.options.rows.find((os) => os.value.id === rpo.value.id)?.optionDefaultsIfIds;
  return (
    <BoundMultiSelectField
      field={formRpoDefaultIds?.value?.nonEmpty ? formRpoDefaultIds : rpo.optionDefaultsIfIds}
      options={mapToRpoSelectFieldOptions(rpo, rpos)}
      placeholder="Default of"
      onSelect={(val) => {
        rpo.optionDefaultsIfIds.set(val);
      }}
      readOnly={readOnly}
    />
  );
}

function mapToRpoSelectFieldOptions(rpo: ObjectState<FormOptionValue>, rpos: CreateOfferingReadyPlanOptionFragment[]) {
  return rpos
    .filter((opt) => opt.optionGroup.id !== rpo.optionGroup.value?.id)
    .sortBy((opt) => opt.name)
    .map((opt) => ({ id: opt.id, name: opt.displayName }));
}

type PublishRPOToUWCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function PublishRPOToUWCheckbox(props: PublishRPOToUWCheckboxProps) {
  const { rpoState, formState, readOnly } = props;
  const selected = useComputed(
    () => Boolean(rpoState.publishToUnderwriting.value),
    [rpoState.publishToUnderwriting.value],
  );
  return (
    <Switch
      selected={selected}
      label="Publish to UW"
      labelStyle="inline"
      disabled={readOnly}
      onChange={(val) => {
        rpoState.publishToUnderwriting.set(val);
        const [updated, same] = formState.options.value.partition((o) => o.id === rpoState.id.value);
        formState.options.set([...same, { ...updated[0], publishToUnderwriting: val }]);
      }}
    />
  );
}

type ShowRPOGInCheckoutCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  usedInBaseConfig?: string;
  rpos: CreateOfferingReadyPlanOptionFragment[];
  childStates: ObjectState<FormOptionValue>[];
};

/** This RPOG-level toggle really just acts as bulk select/unselect all. */
function ShowRPOGInCheckoutCheckbox(props: ShowRPOGInCheckoutCheckboxProps) {
  const { rpoState, formState, readOnly, usedInBaseConfig, rpos, childStates } = props;
  // Filter all `rpos` to only ours
  const groupRpos = rpos.filter((rpo) => rpo.optionGroup.id === rpoState.optionGroup.value?.id);
  const allSelected = useComputed(() => {
    const filteredOptions = formState.options.value
      .filter((opt) => groupRpos.map((rpo) => rpo.id).includes(opt.id))
      .compact();
    return filteredOptions.nonEmpty && filteredOptions.every((opt) => !!opt.showInCheckout);
  }, [groupRpos, formState.options.value]);
  return (
    <Switch
      selected={allSelected}
      label="Checkout"
      labelStyle="inline"
      disabled={readOnly || (allSelected && usedInBaseConfig)}
      onChange={(val) => {
        childStates.forEach((childState) => {
          childState.showInCheckout.set(val);
        });
        const targetIds = groupRpos.map((rpo) => rpo.id);
        const updatedOptions = formState.options.value.map((option) =>
          targetIds.includes(option.id) ? { ...option, showInCheckout: val } : option,
        );
        formState.options.set(updatedOptions);
      }}
    />
  );
}

type ShowRPOInCheckoutCheckboxProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  usedInBaseConfig?: string;
};

function ShowRPOInCheckoutCheckbox(props: ShowRPOInCheckoutCheckboxProps) {
  const { rpoState, formState, readOnly, usedInBaseConfig } = props;
  const selected = useComputed(
    () => Boolean(formState.options.value.find((o) => o.id === rpoState.id.value)?.showInCheckout),
    [rpoState.showInCheckout.value, formState.options.value],
  );
  return (
    <Switch
      selected={selected}
      label="Checkout"
      labelStyle="inline"
      disabled={readOnly || (selected && usedInBaseConfig)}
      onChange={(val) => {
        rpoState.showInCheckout.set(val);
        const targetIds = [rpoState.id.value];
        const updatedOptions = formState.options.value.map((option) =>
          targetIds.includes(option.id) ? { ...option, showInCheckout: val } : option,
        );
        formState.options.set(updatedOptions);
      }}
    />
  );
}

type CheckoutRPOGOrderFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function CheckoutRPOGOrderField(props: CheckoutRPOGOrderFieldProps) {
  const { rpoState, formState, readOnly } = props;
  const { value, showInCheckout } = useComputed(
    () => ({
      value: rpoState.optionGroup.checkoutOrder.value ?? undefined,
      showInCheckout: rpoState.showInCheckout.value,
    }),
    [rpoState],
  );
  return (
    <NumberField
      data-testid="checkoutOrder"
      label="Order"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        // Update the local state for this RPO
        rpoState.optionGroup.checkoutOrder.set(val);
        // Update the optionGroups in formState to track RPOG-level changes
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.checkoutOrder.set(val);
      }}
      readOnly={readOnly || !showInCheckout}
    />
  );
}

type OverrideStepFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  checkoutConfigOptionGroupSteps: CheckoutConfigOptionGroupStepDetail[];
};

function OverrideStepField(props: OverrideStepFieldProps) {
  const { rpoState, formState, readOnly, checkoutConfigOptionGroupSteps } = props;
  const options = [{ code: undefined, name: "Default" }, ...checkoutConfigOptionGroupSteps];
  const value = useComputed(() => rpoState.optionGroup.overrideStep.value, [rpoState]);
  return (
    <SelectField
      data-testid="overrideStep"
      label="Step"
      labelStyle="inline"
      placeholder="Default"
      options={options}
      autoSort={false}
      value={value}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.code}
      onSelect={(val) => {
        const nextValue = val ?? null;
        rpoState.optionGroup.overrideStep.set(nextValue);
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.overrideStep.set(nextValue);
      }}
      // We used to "check showInCheckout" here but:
      // a) the step override is fundamentally an RPOG setting, and the RPOG 'showInCheckout' is
      // really a bulk select, not a true yes/no stored value.
      // b) RPOGs can have partial `showInCheckout` RPOs still might need their step nudged around;
      // maybe if _literally every_ RPO was `showInCheckout=false`, we could say this is disabled,
      // but that's not worth it atm.
      readOnly={readOnly}
    />
  );
}

type OverrideSubstepNumberFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
};

function OverrideSubstepNumberField(props: OverrideSubstepNumberFieldProps) {
  const { rpoState, formState, readOnly } = props;
  const value = useComputed(() => rpoState.optionGroup.overrideSubstepNumber.value ?? undefined, [rpoState]);
  return (
    <NumberField
      data-testid="overrideSubstepNumber"
      label="Substep #"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        const nextValue = val ?? null;
        rpoState.optionGroup.overrideSubstepNumber.set(nextValue);
        const groupState = formState.optionGroups.rows.find((g) => g.value.id === rpoState.optionGroup.id.value);
        groupState?.overrideSubstepNumber.set(nextValue);
      }}
      // See comment on Step field for why we don't check showInCheckout here
      readOnly={readOnly}
      clearable
    />
  );
}

type CheckoutRPOOrderFieldProps = {
  rpoState: ObjectState<FormOptionValue>;
  formState: ObjectState<SaveProductOfferingInput>;
  readOnly: boolean;
  // When true, this RPO belongs to a personalization RPOG, whose checkout order is set in the design package and cannot be edited here.
  isPersonalizationOption: boolean;
};

function CheckoutRPOOrderField(props: CheckoutRPOOrderFieldProps) {
  const { rpoState, formState, readOnly, isPersonalizationOption } = props;
  const { value, showInCheckout } = useComputed(
    () => ({ value: rpoState.checkoutOrder.value ?? undefined, showInCheckout: rpoState.showInCheckout.value }),
    [rpoState],
  );
  return (
    <NumberField
      data-testid="optionCheckoutOrder"
      label="Order"
      labelStyle="inline"
      fullWidth
      value={value}
      onChange={(val) => {
        // Update the local state for this RPO
        rpoState.checkoutOrder.set(val);
        // Update the options in formState to track RPO-level changes
        const optionState = formState.options.rows.find((o) => o.value.id === rpoState.id.value);
        optionState?.checkoutOrder.set(val);
      }}
      readOnly={readOnly || !showInCheckout}
      disabled={
        isPersonalizationOption && "Checkout order of personalization options must be set in the design package."
      }
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };

type ParentRow = {
  kind: "parent";
  id: string;
  data: {
    // This is janky, but the parent uses "the first rpo", instead of the rpog directly
    rpo: CreateOfferingReadyPlanOptionFragment;
    state: ObjectState<FormOptionValue>;
    childStates: ObjectState<FormOptionValue>[];
    usedInBaseConfig?: string;
  };
};

type ChildRow = {
  kind: "child";
  id: string;
  data: { rpo: CreateOfferingReadyPlanOptionFragment; state: ObjectState<FormOptionValue>; usedInBaseConfig?: string };
};

type Row = HeaderRow | ParentRow | ChildRow;

const tableStyles = {
  ...cardStyle,
  cellCss: { ...cardStyle.cellCss, ...Css.aic.pPx(12).$ },
  presentationSettings: { borderless: false },
};

const rowStyles = { child: { rowCss: Css.bgGray100.$, cellCss: Css.bgGray100.$ } };

type ShowOptionsFilterValue = "disabled" | "archived" | "not-in-checkout" | "not-in-underwriting";
type OptionTypeFilter = { option?: string[]; location?: string[]; showOptions?: ShowOptionsFilterValue };

type FormOptionValue = SaveProductOfferingOptionInput & {
  optionGroup: SaveProductOfferingOptionGroupInput;
  order: number;
  checkoutOrder?: number | null;
};

type FormValue = { readyPlanOptions: FormOptionValue[] };

const tableFormConfig: ObjectConfig<FormValue> = {
  readyPlanOptions: {
    type: "list",
    config: {
      id: { type: "value" },
      checkoutOrder: { type: "value" },

      optionGroup: {
        type: "object",
        config: {
          id: { type: "value" },
          checkoutOrder: { type: "value" },
          overrideStep: { type: "value" },
          overrideSubstepNumber: { type: "value" },
        },
      },
      optionDefaultsIfIds: { type: "value" },
      order: { type: "value" },
      publishToUnderwriting: { type: "value" },
      showInCheckout: { type: "value" },
    },
  },
};

type OptionDetailsTagsProps = { rpo: CreateOfferingReadyPlanOptionFragment; isGroupRow?: boolean };

type OptionDetailsTag = { key: string; element: JSX.Element; textValue: string };

/** Helper component that shows the "Details" column of the tags. */
function OptionDetailsTags(props: OptionDetailsTagsProps) {
  const { rpo, isGroupRow = false } = props;
  const tags = optionDetailsTags(rpo, isGroupRow);
  if (tags.isEmpty) return null;
  return (
    <div css={Css.df.fdc.aifs.gap1.$}>
      {tags.map((tag) => (
        <div key={tag.key}>{tag.element}</div>
      ))}
    </div>
  );
}

/** Creates a `OptionDetailsTags` list to be used either in the component or our textValue. */
function optionDetailsTags(rpo: CreateOfferingReadyPlanOptionFragment, isGroupRow: boolean): OptionDetailsTag[] {
  const prerequisites = getOptionDetailsPrerequisites(rpo, isGroupRow);
  const conflicts = rpo.optionConflicts.concat(rpo.optionConflictChildren);
  const showConflicts = !isGroupRow || rpo.optionGroup.isSingleOptionGroup;
  function formatOptionDetailsTextValue(item: { name: string; code?: string }) {
    return [item.name, item.code].compact().join(" ");
  }
  return [
    !isGroupRow && rpo.optionGroup.name
      ? {
          key: `group-${rpo.optionGroup.id}`,
          element: <Tag text={rpo.optionGroup.name} type="info" />,
          textValue: rpo.optionGroup.name,
        }
      : undefined,
    rpo.type.name
      ? {
          key: `type-${rpo.type.id}-${isGroupRow ? "group" : "child"}`,
          element: isGroupRow ? (
            <Tag
              text={rpo.type.name}
              type={rpo.optionGroup.isSingleOptionGroup ? undefined : "info"}
              xss={rpo.optionGroup.isSingleOptionGroup ? Css.bgPurple200.$ : undefined}
            />
          ) : (
            <Tag text={rpo.type.name} xss={Css.bgPurple200.$} />
          ),
          textValue: rpo.type.name,
        }
      : undefined,
    prerequisites.nonEmpty
      ? {
          key: `prerequisites-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <PrerequisitesTag prerequisites={prerequisites} />,
          textValue: prerequisites.map((prerequisite) => formatOptionDetailsTextValue(prerequisite)).join(" "),
        }
      : undefined,
    showConflicts && conflicts.nonEmpty
      ? {
          key: `conflicts-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <ConflictTag rpo={rpo} />,
          textValue: conflicts.map((conflict) => formatOptionDetailsTextValue(conflict)).join(" "),
        }
      : undefined,
    rpo.optionRequiredIf.nonEmpty
      ? {
          key: `required-options-${rpo.id}-${isGroupRow ? "group" : "child"}`,
          element: <RequiredOptionsTag optionRequiredIf={rpo.optionRequiredIf} />,
          textValue: rpo.optionRequiredIf
            .flatMap((rpro) => rpro.targetCombinations)
            .map((combo) => combo.options.map((o) => o.name).join("/"))
            .join(", "),
        }
      : undefined,
  ].compact();
}

function getOptionDetailsPrerequisites(rpo: CreateOfferingReadyPlanOptionFragment, isGroupRow: boolean) {
  if (!isGroupRow) {
    return rpo.optionPrerequisites;
  }
  return rpo.optionGroup.isSingleOptionGroup ? rpo.optionPrerequisites : rpo.optionGroup.groupPrerequisites;
}

/**
 * Highlight option rows (and singleOptionGroup rows) if the option is newly added from upstream
 * And while in the edit path (which is only allowed for the latest version)
 * (This options table is also used in the create path, and for older versions)
 */
function showHighlights({
  rpo,
  isEditPath,
  isGroupRow = false,
}: {
  rpo: CreateOfferingReadyPlanOptionFragment;
  isEditPath: boolean;
  isGroupRow?: boolean;
}) {
  const renderHighlights =
    (!!rpo.draftVersion?.isNewFromUpstream || !!rpo.draftVersion?.isUpstreamInactive) && isEditPath;
  return renderHighlights && (!isGroupRow || !!rpo.optionGroup.isSingleOptionGroup);
}
